import {
	applyContrastCorrection,
	expandStyleConfig,
	parseStyleConfigJson,
} from '@/lib/style-config';

/**
 * Apply CSS variables from JSON config to .tailwind selector.
 * Variables are scoped to .tailwind to avoid interfering with the theme.
 * Supports both light mode (.tailwind) and dark mode (.tailwind.dark, .dark .tailwind).
 *
 * Unless `autoContrast` is false, the text colour of the user bubble, the send
 * button and the error button is replaced by black or white when the configured
 * pair is below 4.5:1. Only the generated rule changes, never the stored JSON.
 *
 * @param styleConfigJson      - JSON string containing CSS variable overrides
 * @param options              - `autoContrast` (default true) switches the correction
 * @param options.autoContrast
 */
export function applyStyleConfig(
	styleConfigJson: string,
	options: { autoContrast?: boolean } = {}
): void {
	if ( ! styleConfigJson || typeof styleConfigJson !== 'string' ) {
		return;
	}

	const trimmed = styleConfigJson.trim();
	if ( ! trimmed ) {
		return;
	}

	try {
		const config = expandStyleConfig(
			applyContrastCorrection(
				parseStyleConfigJson( trimmed ),
				options.autoContrast !== false
			)
		);
		if ( Object.keys( config ).length === 0 ) {
			return;
		}

		// Get or create style element for .tailwind
		let styleElement = document.getElementById(
			'dhsv100-ai-chat-plugin-style-config'
		) as HTMLStyleElement | null;

		if ( ! styleElement ) {
			styleElement = document.createElement( 'style' );
			styleElement.id = 'dhsv100-ai-chat-plugin-style-config';
			document.head.appendChild( styleElement );
		}

		// Build CSS rules - apply all variables to .tailwind
		const rules: string[] = [];

		Object.entries( config ).forEach( ( [ key, value ] ) => {
			if ( typeof key !== 'string' || typeof value !== 'string' ) {
				return;
			}

			// Ensure key starts with --
			const cssVar = key.startsWith( '--' ) ? key : `--${ key }`;

			// Add rule
			rules.push( `  ${ cssVar }: ${ value };` );
		} );

		// Build CSS text - apply to .tailwind and .dark .tailwind for dark mode support
		let cssText = '';
		if ( rules.length > 0 ) {
			cssText += `.tailwind {\n${ rules.join( '\n' ) }\n}\n\n`;
			// Also apply to dark mode variants
			cssText += `.tailwind.dark,\n.dark .tailwind {\n${ rules.join(
				'\n'
			) }\n}\n`;
		}

		styleElement.textContent = cssText;
	} catch ( error ) {
		// Silently fail if JSON is invalid (shouldn't happen as it's validated on backend)
		// eslint-disable-next-line no-console
		console.warn( 'Failed to apply style config:', error );
	}
}
